import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Camera,
  Layers,
  ShoppingBag,
  ChevronRight,
  Heart,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, LoadingState, ErrorState, Thumb, FitThumb } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { formatCurrency, formatRelativeDate } from '@/lib/utils';
import type { DashboardStats } from '@/lib/types';

export function HomePage() {
  const { data: stats, loading, error } = useApi<DashboardStats>('/dashboard/stats');
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  return (
    <Page>
      <PageHeader section="Home" />
      <Main className="space-y-5">
        <form onSubmit={onSearch} className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your wardrobe…"
            className="w-full pl-10 pr-4 py-3 bg-card border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </form>

        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {stats && (
          <>
            {/* Quick actions */}
            <div>
              <h2 className="text-base font-semibold text-foreground uppercase tracking-wide mb-3 font-display">Quick Actions</h2>
              <div className="grid grid-cols-2 gap-3">
                <QuickAction icon={<Plus className="w-5 h-5" />} label="Add Item" to="/wardrobe/add" />
                <QuickAction icon={<Camera className="w-5 h-5" />} label="Camera" to="/camera" />
                <QuickAction icon={<Layers className="w-5 h-5" />} label="Build Fit" to="/fits/builder/sessions" />
                <QuickAction icon={<ShoppingBag className="w-5 h-5" />} label="Before You Buy" to="/before-buy" />
              </div>
            </div>

            {/* Recent fits */}
            {stats.recentFits.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-semibold text-foreground uppercase tracking-wide font-display">Recent Fits</h2>
                  <Link to="/fits" className="text-xs text-primary font-medium">
                    See all
                  </Link>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {stats.recentFits.map((fit) => (
                    <Link key={fit.id} to={`/fits/${fit.id}`} className="flex-shrink-0 w-28">
                      <FitThumb url={fit.imageUrl} piecePhotoUrls={fit.piecePhotoUrls} alt={fit.name} className="w-28 h-28 rounded-2xl" />
                      <p className="text-xs font-medium text-foreground mt-1.5 line-clamp-1">{fit.name}</p>
                      <p className="text-[11px] text-muted-foreground">{formatRelativeDate(fit.lastWornAt)}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Wardrobe desires / wishlist */}
            {stats.wishlistPreview.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-semibold text-foreground uppercase tracking-wide flex items-center gap-1.5 font-display">
                    <Heart className="w-3.5 h-3.5" /> Wardrobe Desires
                  </h2>
                  <Link to="/wardrobe/items?status=wishlist" className="text-xs text-primary font-medium">
                    See all
                  </Link>
                </div>
                <div className="space-y-3">
                  {stats.wishlistPreview.map((item) => (
                    <Link key={item.id} to={`/wardrobe/item/${item.id}`}>
                      <Card className="p-3 flex items-center gap-3">
                        <Thumb url={item.primaryPhotoUrl} alt={item.name} className="w-14 h-14 rounded-xl flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground line-clamp-1">{item.name}</p>
                          {item.brand && <p className="text-xs text-muted-foreground">{item.brand}</p>}
                        </div>
                        <span className="text-sm font-semibold text-foreground">{formatCurrency(item.cost)}</span>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Main>
    </Page>
  );
}

function QuickAction({ icon, label, to }: { icon: React.ReactNode; label: string; to: string }) {
  return (
    <Link to={to}>
      <Card className="p-4 flex items-center gap-3 hover:border-primary/30 transition-colors">
        <div className="p-2 bg-secondary rounded-lg text-primary">{icon}</div>
        <span className="text-sm font-medium text-foreground">{label}</span>
        <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
      </Card>
    </Link>
  );
}
